import { useEffect, useMemo, useState } from 'react';
import api from '../services/api';
import { errorMessage } from '../lib/http';
import { formatBytes, formatDateTime } from '../lib/format';
import { BTN_PRIMARY, BTN_PRIMARY_STYLE, BTN_SOFT, BTN_SOFT_STYLE, FIELD, FIELD_STYLE, LABEL } from '../lib/ui';
import { Card, Notice, Spinner, StatusPill } from './ui';
import ConfirmDialog from './ConfirmDialog';
import type { Lead } from './PartnerLeads';
import type { CompanyDocument, Requirement } from '../types/workspace';

type Partner = { id: string; name: string; email: string; firm: string | null; focus: string | null; status: string };
type GeneratedId = 'ready_pdf' | 'rating_report';

type DocRow = {
  key: string;
  kind: 'generated' | 'customer';
  generatedId?: GeneratedId;
  documentId?: string;
  title: string;
  hint: string;
  mandate: boolean;
  meta?: string;
};

const GENERATED_DOCS: { id: GeneratedId; title: string; hint: string; mandate: boolean }[] = [
  {
    id: 'ready_pdf',
    title: 'Approved Ready summary (PDF)',
    hint: 'Micus-generated — company overview, funding ask, financials and risks',
    mandate: true,
  },
  {
    id: 'rating_report',
    title: 'Micus credit rating report (PDF)',
    hint: 'Micus-generated — published grade, summary and Major Concerns',
    mandate: true,
  },
];

const PARTNER_PAGE_SIZE = 5;

type Preview = { to: string; partnerName: string; subject: string; html: string; attachments: string[] };

/** Cold email: pick remaining partners + documents, preview, send. Already-sent partners stay disabled. */
export default function PartnerSendPanel({
  companyId,
  partners,
  leads,
  documents,
  requirements,
  reportVersion,
  approvedAt,
  onSent,
  onError,
}: {
  companyId: string;
  partners: Partner[];
  leads: Lead[];
  documents: CompanyDocument[];
  requirements: Requirement[];
  reportVersion: number;
  approvedAt?: string | null;
  onSent: (message: string) => void;
  onError: (message: string) => void;
}) {
  const sentIds = useMemo(() => new Set(leads.map((l) => l.partner.id)), [leads]);
  const available = useMemo(() => partners.filter((p) => p.status !== 'PAUSED'), [partners]);
  const remaining = useMemo(() => available.filter((p) => !sentIds.has(p.id)), [available, sentIds]);
  const requiredCodes = useMemo(() => new Set(requirements.filter((r) => r.required).map((r) => r.code)), [requirements]);
  const shareableDocs = useMemo(
    () => documents.filter((d) => d.status !== 'REJECTED' && d.status !== 'SUPERSEDED'),
    [documents],
  );

  const docRows = useMemo<DocRow[]>(() => {
    const generated: DocRow[] = GENERATED_DOCS.map((g) => ({
      key: `gen:${g.id}`,
      kind: 'generated',
      generatedId: g.id,
      title: g.title,
      hint: g.hint,
      mandate: g.mandate,
      meta: 'Micus generated',
    }));
    const customer: DocRow[] = shareableDocs.map((d) => {
      const mandate = requiredCodes.has(d.code);
      return {
        key: `doc:${d.id}`,
        kind: 'customer',
        documentId: d.id,
        title: d.title,
        hint: d.fileName,
        mandate,
        meta: `${formatBytes(d.sizeBytes)} · ${d.status}${mandate ? ' · Mandate' : ''}`,
      };
    });
    return [...generated, ...customer];
  }, [shareableDocs, requiredCodes]);

  const defaultDocKeys = useMemo(() => docRows.filter((r) => r.mandate).map((r) => r.key), [docRows]);

  const [selected, setSelected] = useState<string[]>([]);
  const [selectedDocs, setSelectedDocs] = useState<string[]>([]);
  const [partnerQuery, setPartnerQuery] = useState('');
  const [partnerPage, setPartnerPage] = useState(1);
  const [note, setNote] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [confirmSend, setConfirmSend] = useState(false);
  const [busy, setBusy] = useState<'preview' | 'send' | null>(null);

  useEffect(() => {
    setSelected(remaining.map((p) => p.id));
  }, [partners, leads]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setSelectedDocs(defaultDocKeys);
    setPreview(null);
  }, [defaultDocKeys]);

  const filteredPartners = useMemo(() => {
    const q = partnerQuery.trim().toLowerCase();
    if (!q) return available;
    return available.filter((p) => [p.name, p.firm, p.email, p.focus].some((v) => v?.toLowerCase().includes(q)));
  }, [available, partnerQuery]);

  const partnerPageCount = Math.max(1, Math.ceil(filteredPartners.length / PARTNER_PAGE_SIZE));
  const safePartnerPage = Math.min(partnerPage, partnerPageCount);
  const pagedPartners = filteredPartners.slice((safePartnerPage - 1) * PARTNER_PAGE_SIZE, safePartnerPage * PARTNER_PAGE_SIZE);

  useEffect(() => {
    setPartnerPage(1);
  }, [partnerQuery]);

  const selectionPayload = useMemo(() => {
    const documentIds: string[] = [];
    const generatedIds: GeneratedId[] = [];
    for (const key of selectedDocs) {
      const row = docRows.find((r) => r.key === key);
      if (!row) continue;
      if (row.kind === 'generated' && row.generatedId) generatedIds.push(row.generatedId);
      if (row.kind === 'customer' && row.documentId) documentIds.push(row.documentId);
    }
    return { documentIds, generatedIds };
  }, [selectedDocs, docRows]);

  const togglePartner = (id: string) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setPreview(null);
  };

  const toggleDoc = (key: string) => {
    setSelectedDocs((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
    setPreview(null);
  };

  const runPreview = async () => {
    if (!selected.length) {
      onError('Select at least one partner who has not been emailed yet.');
      return;
    }
    if (!selectionPayload.documentIds.length && !selectionPayload.generatedIds.length) {
      onError('Select at least one document.');
      return;
    }
    setBusy('preview');
    try {
      const { data } = await api.post(`/admin/companies/${companyId}/share/preview`, {
        partnerIds: selected,
        ...selectionPayload,
        note: note || null,
      });
      setPreview({
        to: data.to,
        partnerName: data.partnerName,
        subject: data.subject,
        html: data.html,
        attachments: data.attachments || [],
      });
    } catch (err) {
      onError(errorMessage(err, 'Could not build email preview.'));
    } finally {
      setBusy(null);
    }
  };

  const send = async () => {
    if (!selected.length || !preview) return;
    setBusy('send');
    try {
      const { data } = await api.post(
        `/admin/companies/${companyId}/share`,
        { partnerIds: selected, ...selectionPayload, note: note || null },
        { timeout: 180000 },
      );
      onSent(`Sent to ${data.total} partner(s) — ${data.sent} email(s) delivered.`);
      setPreview(null);
      setConfirmSend(false);
    } catch (err) {
      onError(errorMessage(err));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-5">
      <div className="rounded-2xl px-4 py-3" style={{ background: '#fffafc', border: '1px solid #fce7f3' }}>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill label={`Customer approved · v${reportVersion}`} tone="green" />
          {approvedAt && <span className="text-[11px] text-[#b0708f]">{formatDateTime(approvedAt)}</span>}
          <StatusPill label={`${remaining.length} remaining`} tone={remaining.length ? 'pink' : 'slate'} />
        </div>
        <p className="mt-2 text-sm text-[#6b3a52]">
          Send the cold email pack to partners who have not received this rating yet. Already-sent partners stay listed but cannot be selected again.
        </p>
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <Card title="1 · Partners" subtitle="Who receives this cold email.">
          <input
            value={partnerQuery}
            onChange={(e) => setPartnerQuery(e.target.value)}
            placeholder="Search name, firm, email…"
            className={`${FIELD} mb-3`}
            style={FIELD_STYLE}
          />
          <ul className="max-h-80 space-y-1.5 overflow-y-auto">
            {filteredPartners.length === 0 && (
              <li className="text-sm text-[#c084a0]">
                {available.length === 0 ? 'No partners yet. Add them in Admin → Partners.' : 'No partners match this search.'}
              </li>
            )}
            {pagedPartners.map((p) => {
              const sent = sentIds.has(p.id);
              const checked = selected.includes(p.id);
              return (
                <li key={p.id}>
                  <label
                    className={`flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5 ${sent ? 'opacity-55' : ''}`}
                    style={{ border: `1px solid ${checked && !sent ? '#f9a8d4' : '#fce7f3'}`, background: checked && !sent ? '#fff5f8' : '#fff' }}
                  >
                    <input
                      type="checkbox"
                      className="mt-1"
                      disabled={sent}
                      checked={checked && !sent}
                      onChange={() => !sent && togglePartner(p.id)}
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold text-[#6b0030]">
                        {p.name}
                        {p.firm ? ` · ${p.firm}` : ''}
                      </span>
                      <span className="block truncate text-[11px] text-[#c084a0]">
                        {p.email}
                        {p.focus ? ` · ${p.focus}` : ''}
                      </span>
                    </span>
                    {sent ? <StatusPill label="Already sent" tone="green" /> : null}
                  </label>
                </li>
              );
            })}
          </ul>
          {filteredPartners.length > 0 && (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] text-[#b0708f]">
              <span>
                Showing {(safePartnerPage - 1) * PARTNER_PAGE_SIZE + 1}–
                {Math.min(safePartnerPage * PARTNER_PAGE_SIZE, filteredPartners.length)} of {filteredPartners.length}
                {selected.length ? ` · ${selected.length} selected` : ''}
              </span>
              <div className="flex items-center gap-1">
                <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} disabled={safePartnerPage <= 1} onClick={() => setPartnerPage((p) => Math.max(1, p - 1))}>
                  Prev
                </button>
                <span className="px-1 font-semibold text-[#6b0030]">
                  {safePartnerPage}/{partnerPageCount}
                </span>
                <button
                  type="button"
                  className={BTN_SOFT}
                  style={BTN_SOFT_STYLE}
                  disabled={safePartnerPage >= partnerPageCount}
                  onClick={() => setPartnerPage((p) => Math.min(partnerPageCount, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          )}
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={BTN_SOFT}
              style={BTN_SOFT_STYLE}
              onClick={() => {
                setSelected(remaining.map((p) => p.id));
                setPreview(null);
              }}
            >
              Select remaining
            </button>
            <button
              type="button"
              className={BTN_SOFT}
              style={BTN_SOFT_STYLE}
              onClick={() => {
                setSelected([]);
                setPreview(null);
              }}
            >
              Clear
            </button>
          </div>
        </Card>

        <Card title="2 · Documents" subtitle="Attached to the cold email. Mandate docs are pre-selected.">
          <ul className="max-h-80 space-y-2 overflow-y-auto">
            {docRows.map((row) => {
              const checked = selectedDocs.includes(row.key);
              return (
                <li key={row.key}>
                  <label
                    className="flex cursor-pointer items-start gap-3 rounded-xl px-3 py-2.5"
                    style={{ border: `1px solid ${checked ? '#f9a8d4' : '#fce7f3'}`, background: checked ? '#fff5f8' : '#fff' }}
                  >
                    <input type="checkbox" className="mt-1" checked={checked} onChange={() => toggleDoc(row.key)} />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-[#6b0030]">{row.title}</span>
                        {row.mandate ? <StatusPill label="Mandate" tone="pink" /> : null}
                        {row.kind === 'generated' ? <StatusPill label="Micus" tone="slate" /> : null}
                      </span>
                      <span className="mt-0.5 block text-[11px] text-[#c084a0]">{row.hint}</span>
                      {row.meta ? <span className="mt-0.5 block text-[10px] font-semibold uppercase tracking-wider text-[#d4a0b8]">{row.meta}</span> : null}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} onClick={() => { setSelectedDocs(defaultDocKeys); setPreview(null); }}>
              Select mandate
            </button>
            <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} onClick={() => { setSelectedDocs(docRows.map((r) => r.key)); setPreview(null); }}>
              Select all
            </button>
            <button type="button" className={BTN_SOFT} style={BTN_SOFT_STYLE} onClick={() => { setSelectedDocs([]); setPreview(null); }}>
              Clear
            </button>
          </div>
          <label className={`${LABEL} mt-4`}>Note to partners (optional)</label>
          <textarea
            rows={3}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setPreview(null);
            }}
            className={`${FIELD} resize-y`}
            style={FIELD_STYLE}
            placeholder="Why this company is worth a look…"
          />
        </Card>
      </div>

      <Card
        title="3 · Preview & send"
        subtitle="Preview uses the first selected partner. Send goes only to partners not emailed yet."
        action={
          <div className="flex flex-wrap gap-2">
            <button type="button" disabled={busy !== null || !selected.length} onClick={() => void runPreview()} className={BTN_SOFT} style={BTN_SOFT_STYLE}>
              {busy === 'preview' && <Spinner className="h-3.5 w-3.5" />} Preview email
            </button>
            <button type="button" disabled={busy !== null || !preview} onClick={() => setConfirmSend(true)} className={BTN_PRIMARY} style={BTN_PRIMARY_STYLE}>
              {busy === 'send' && <Spinner />} Send to {selected.length || 0} partner{selected.length === 1 ? '' : 's'}
            </button>
          </div>
        }
      >
        {!remaining.length ? (
          <Notice tone="green">Every active partner has already been emailed for this rating version.</Notice>
        ) : !preview ? (
          <Notice tone="amber">Select remaining partners and documents, then preview before sending.</Notice>
        ) : (
          <div className="space-y-3">
            <dl className="grid gap-2 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Preview as</dt>
                <dd className="font-semibold text-[#6b0030]">
                  {preview.partnerName} · {preview.to}
                </dd>
              </div>
              <div>
                <dt className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Subject</dt>
                <dd className="font-semibold text-[#6b0030]">{preview.subject}</dd>
              </div>
            </dl>
            {preview.attachments.length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#c084a0]">Attachments ({preview.attachments.length})</p>
                <ul className="mt-1 list-inside list-disc text-xs text-[#6b3a52]">
                  {preview.attachments.map((a) => (
                    <li key={a}>{a}</li>
                  ))}
                </ul>
              </div>
            )}
            <div className="overflow-hidden rounded-xl" style={{ border: '1px solid #fce7f3', background: '#faf7f9' }}>
              <iframe title="Partner email preview" srcDoc={preview.html} className="h-[480px] w-full bg-white" />
            </div>
          </div>
        )}
      </Card>

      <ConfirmDialog
        open={confirmSend}
        title={`Send cold email to ${selected.length} partner${selected.length === 1 ? '' : 's'}?`}
        description={`This emails the previewed pack${preview?.attachments.length ? ` with ${preview.attachments.length} attachment${preview.attachments.length === 1 ? '' : 's'}` : ''} to the selected partners. Already-sent partners are skipped.`}
        confirmLabel={selected.length === 1 ? 'Send to 1 partner' : `Send to ${selected.length} partners`}
        cancelLabel="Cancel"
        busy={busy === 'send'}
        onCancel={() => busy !== 'send' && setConfirmSend(false)}
        onConfirm={() => void send()}
      />
    </div>
  );
}
